import { trackPromise } from "react-promise-tracker";
import CryptoJS from "crypto-js";
import { useSelector } from "react-redux";
import { selectUserDetail } from "../../redux/slice/userSlice";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
} from "@mui/material";
import moment from "moment/moment";
import apiClient from "../../lib/api";
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";
import GridComponent from "../../shared-components/gridComponent";
import { useLocation, useNavigate } from "react-router-dom";
import { IconEye, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import dayjs from "dayjs";
import ForecastHoursDetails from "./forecastHoursDetails";

const SubmissionList = () => {
  const navigate = useNavigate();
  const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [gridKey, setGridKey] = useState(false);
  const getUserdetail = useSelector(selectUserDetail);
  const [selectedRow, setSelectedRow] = useState(null);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [filterDropdownValue, setFilterDropdownValue] = useState();
  const fromMarketersLocation = useLocation();
  const { status, marketercode } = fromMarketersLocation.state || {};
  const [clientExistsInWellSkyDialog, setClientExistsInWellSkyDialog] = useState(false);
  const [existingClientType, setExistingClientType] = useState("")
  const [isDownloading, setIsDownloading] = useState(false);
  const [openForecastHoursDetailsModal, setForecastHoursDetailsModal] = useState(false);
  const isAdmin = getUserdetail?.roles === dataConstant.roles.admin;

  useEffect(() => {
    trackPromise(
      apiClient
        .get(`${urlConstant.Submission.GetAllFilterDropDowns}`, {
          authRequired: false,
        })
        .then((response) => {
          if (response.status === dataConstant.APIStatus.Success) {
            setFilterDropdownValue(response?.data);
          }
        })
    );
  }, []);

  const advanceFiltersConfig = [
    {
      type: "date-range",
      label: "Application",
      name: "applicationDate",
    },
    {
      type: "select",
      label: "Community Liaison",
      name: "liaison",
      options: filterDropdownValue?.liasionsData,
    },
    {
      type: "text",
      label: "Referred By",
      name: "refferedBy",
    },
  ];

  const baseColumns = [
    {
      field: " ",
      headerName: "",
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            size="small"
            onClick={(e) => {
              if(getUserdetail?.roles === dataConstant.roles.admin){
                navigate(`/manageSubmission/${params.row.id}`);
              }else{
                navigate(`/submissionDetail/${params.row.id}`);
              }
            }}
          >
            <IconEye stroke={1.5} />
          </IconButton>
        </>
      ),
    },
    {
      field: "submissionId",
      headerName: "Submission No.",
      flex: 1,
      sortable: true,
    },
    {
      field: "fullName",
      headerName: "Full Name",
      flex: 1,
      sortable: true,
    },
    {
      field: "communityLiaison",
      headerName: "Community Liaison",
      flex: 1,
      sortable: false,
    },
    ...(isAdmin ? [
      {
        field: "totalHours",
        headerName: "Forecast Hours",
        flex: 1,
        sortable: false,
        renderCell:(params) => (
          <>
          <Button
              variant="contained"
              size="small"
              onClick={() => {
                setSelectedRow(params.row);
                setForecastHoursDetailsModal(true);
              }}
            >
              {params.row.totalHours ?? 0.00}
            </Button>
          </>
        )
      }
    ]:[]),
    {
      field: "referredBy",
      headerName: "Referred By",
      flex: 1,
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: false,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: "applicationDate",
      headerName: "Application At",
      flex: 1,
      sortable: true,
    },
  ];

  const actionColumn = {
    field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setSelectedRow(params.row);
              setConfirmOpen(true);
            }}
          >
            Approve
          </Button>
          <IconButton
            onClick={() => {
              setSelectedRow(params.row);
              setDeleteConfirmOpen(true);
            }}
          >
            <IconX stroke={1.5} />{" "}
          </IconButton>
        </>
    ),
  }

  const columns =
    getUserdetail?.roles === dataConstant.roles.marketer
      ? baseColumns
      : [...baseColumns, actionColumn];

  const getGridData = async ({
    page,
    pageSize,
    sortModel,
    searchText,
    filters,
  }) => {
    const sort =
      sortModel.length > 0
        ? {
          sortColumnName: sortModel[0].field,
          sortOrder: sortModel[0].sort === "asc" ? "asc" : "desc",
        }
        : { sortColumnName: "applicationDate", sortOrder: "desc" };
    const requestObject = {
      PagingInfoVM: {
        pageNumber: page,
        rowsPerPage: pageSize,
        searchText: searchText,
        sortColumns: sort,
      },
    };
    if (filters?.applicationDate) {
      requestObject.FromDate = dayjs(filters?.applicationDate?.from).format(
        dataConstant.dateFormat
      );
      requestObject.ToDate = dayjs(filters?.applicationDate?.to).format(
        dataConstant.dateFormat
      );
    }
    if (filters?.liaison) {
      requestObject.LiaisonName = filters?.liaison;
    }
    if (filters?.refferedBy) {
      requestObject.RefferedBy = filters?.refferedBy;
    }
    if (status === "isForPending" && marketercode) {
      requestObject.MarketerCode = marketercode;
    }
    try {
      const response = await trackPromise(
        apiClient.post(
          urlConstant.Submission.GetPendingSubmissionList,
          requestObject,
          { authRequired: true }
        )
      );

      if (response?.status !== dataConstant.APIStatus.Success) {
        showSnackbar(response?.message, "error");
        return {
          items: [],
          totalCount: 0,
        };
      }
      const decryptedData = response.data?.pendingSubmissionLists?.map(
        (item) => ({
          ...item,
          id: item.unqGuid,
          applicationDate:
            item.applicationDate !== null
              ? moment
                .utc(item.applicationDate)
                .tz(dataConstant.centralAmericaTimeZone)
                .format(dataConstant.dateFormatWithTime)
              : "-",
        })
      );
      return {
        items: decryptedData || [],
        totalCount: response.data?.totalCount || 0,
      };
    } catch (err) {
      console.error("API failed", err);
      return {
        items: [],
        totalCount: 0,
      };
    }
  };

  const approveSubmissionHandle = (clientExistsInWellSky) => {
    trackPromise(
      apiClient
        .get(
          `${urlConstant.Submission.ApprovedSubmission}?submissionUnqGuid=${selectedRow?.id}&checkClient=${clientExistsInWellSky}`,
          {
            authRequired: true,
          }
        )
        .then((response) => {
          if (response.status !== dataConstant.APIStatus.Success) {
            showSnackbar(response.message, "error");
            setConfirmOpen(false);
          } else {
            setConfirmOpen(false);
            if ((response?.data === "client" || response?.data === "prospect") && !clientExistsInWellSky) {
              setClientExistsInWellSkyDialog(true);
              setExistingClientType(response?.data)
            } else {
              setClientExistsInWellSkyDialog(false);
              showSnackbar(response.message, "success");
              setGridKey(!gridKey);
            }
          }
        })
    );
  };

  const deleteSubmissionHandle = () => {
    const request = {
      UnqGuid: selectedRow.id,
      UserId: getUserdetail.userId,
    };
    trackPromise(
      apiClient
        .delete(`${urlConstant.Submission.DeleteSubmission}`, {
          authRequired: true,
          data: request,
        })
        .then((response) => {
          if (response.status !== dataConstant.APIStatus.Success) {
            showSnackbar(response.message, "error");
            setDeleteConfirmOpen(false);
          } else {
            showSnackbar(response.message, "success");
            setGridKey(!gridKey);
            setDeleteConfirmOpen(false);
          }
        })
    );
  };
  const printableFormDownload = async () => {
    try {
      setIsDownloading(true);

      const response = await apiClient.get(
        `${urlConstant.Submission.SubmissionAttachment}`,
        { authRequired: false }
      );

      if (!response || !response.data || !response.data.fileData) {
        throw new Error("File not found in response");
      }

      const { fileName, fileData } = response.data;

      // Convert Base64 to Blob
      const byteCharacters = atob(fileData);
      const byteNumbers = new Array(byteCharacters.length)
        .fill()
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: 'application/pdf'});
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setIsDownloading(false);
    }
  };



  return (
    <>
      <GridComponent
        columns={columns}
        getGridData={getGridData}
        gridKey={gridKey}
        isRequiredAdvanceFilter={true}
        isDownloadButton={true}
        addDownloadTitle={isDownloading ? "Downloading..." : "Printable Form"}
        advanceFiltersConfig={advanceFiltersConfig}
        onDownload={printableFormDownload}
        downloadLoading={isDownloading}
      />

      <ForecastHoursDetails open={openForecastHoursDetailsModal} onClose={()=>setForecastHoursDetailsModal(false)} scheduleData = {selectedRow?.foreCastHours || []}/>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
        maxWidth="xs"
      >
        <DialogContent>
          <DialogContentText
            typography="h5"
            color="default.main"
            id="reset-dialog-description"
            textAlign="center"
          >
            Are you sure you want to approve and submit this data for{" "}
            {selectedRow?.fullName} to Well Sky?
          </DialogContentText>
          <DialogActions
            sx={{ padding: 0, justifyContent: "center", mt: 4, border: "none" }}
          >
            <Button
              size="small"
              onClick={() => approveSubmissionHandle(false)}
              color="primary"
              variant="contained"
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setConfirmOpen(false);
              }}
              color="primary"
              variant="outlined"
            >
              No
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
        maxWidth="xs"
      >
        <DialogContent>
          <DialogContentText
            typography="h5"
            color="default.main"
            id="reset-dialog-description"
            textAlign="center"
          >
            Are you sure you want to delete {selectedRow?.fullName}'s to record?
          </DialogContentText>
          <DialogActions
            sx={{ padding: 0, justifyContent: "center", mt: 4, border: "none" }}
          >
            <Button
              size="small"
              onClick={() => deleteSubmissionHandle()}
              color="primary"
              variant="contained"
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setDeleteConfirmOpen(false);
              }}
              color="primary"
              variant="outlined"
            >
              No
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      <Dialog
        open={clientExistsInWellSkyDialog}
        onClose={() => setClientExistsInWellSkyDialog(false)}
      >
        <DialogContent>
          <DialogContentText
            typography="body1"
            color="default.main"
            id="reset-dialog-description"
            textAlign="center"
          >
              A {existingClientType} with the name <strong>{selectedRow?.fullName}</strong>, 
              address <strong>{CryptoJS.AES.decrypt(selectedRow?.address ?? "", process.env.REACT_APP_ENCRYPTION_KEY)?.toString(CryptoJS.enc.Utf8)}</strong>, 
              and city <strong>{CryptoJS.AES.decrypt(selectedRow?.city ?? "", process.env.REACT_APP_ENCRYPTION_KEY)?.toString(CryptoJS.enc.Utf8)}</strong>{" "}
              already exists in <strong>WellSky</strong>.
              <br />
              Proceeding will <strong>update</strong> the existing {existingClientType} record.
              <br />
              Do you want to continue?
          </DialogContentText>

          <DialogActions
            sx={{ padding: 0, justifyContent: "center", mt: 4, border: "none" }}
          >
            <Button
              size="small"
              onClick={() => approveSubmissionHandle(true)}
              color="primary"
              variant="contained"
            >
              Yes, Update {existingClientType}
            </Button>
            <Button
              size="small"
              onClick={() => setClientExistsInWellSkyDialog(false)}
              color="primary"
              variant="outlined"
              sx={{ ml: 2 }}
            >
              Cancel
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      <CommonSnackBar
        snackObj={snack}
        closeSnack={closeSnack}
        duration={2000}
      />
    </>
  );
};

export default SubmissionList;
